/**
 * The one read the auto-approval screens share.
 *
 * Three screens need the same window of decisions with the same filters — the
 * run table, the decision log and the policy panel — so the fetching,
 * the filter state and the degradation rules live here once rather than three
 * times. A screen picks the columns; none of them talks to the service.
 *
 * WHAT DEGRADES AND WHAT DOES NOT:
 *
 *   rows     — the screen. A failure here is handed up as an error.
 *   summary  — the counter cards. Settled separately, because counters that
 *              are briefly missing are better than a log that is missing.
 *   30-day overrides — the "policy disagreements" card alone, same reasoning.
 *
 * The disagreement count is its own request on purpose. The design fixes that
 * card's window at 30 days ("humans overriding the agent, 30d") while the rest
 * of the screen follows whatever window is selected, so it cannot be read off
 * the same payload. `limit: 1` because only `total` is wanted — the rows for
 * it are a screen of their own (the log, filtered).
 *
 * It is OPT-IN (`withDisagreements`) because only the run screen shows that
 * card. The log and the policy screen use the same feed and would otherwise
 * each make a request whose answer they never render.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  fetchPartnerDecisions,
  fetchPartnerDecisionSummary,
} from '../../../Services/PartnerIntegrationService';
import PartnersService from '../../../Services/PartnersService';

export const DECISION_PAGE_LIMIT = 50;

/** The window the "policy disagreements" card counts over, per the design. */
export const DISAGREEMENT_WINDOW_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

export const sinceIso = (days) => new Date(Date.now() - (Number(days) * DAY_MS)).toISOString();

export const SINCE_OPTIONS = [
  { value: '1', label: 'Last 24 hours' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
];

/**
 * The window in words, for a counter card's own label.
 *
 * The design labels the first card "Auto-approved today". It is only "today"
 * on the 24-hour window, and a 30-day count under a "today" label is a wrong
 * number with a confident label — the one outcome worth avoiding most. So the
 * card takes its period from the filter and says which one it is.
 */
export const windowLabel = (days) => (SINCE_OPTIONS
  .find((option) => option.value === String(days))?.label || `Last ${days} days`)
  .toLowerCase();

const EMPTY_SUMMARY = { byDecision: {}, byHeldReason: {} };

export default function useDecisionFeed({ onError, withDisagreements = false } = {}) {
  const [partners, setPartners] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  // null, not 0: "we could not count the disagreements" and "there were none"
  // are different facts and the card renders them differently.
  const [disagreements, setDisagreements] = useState(null);
  const [decision, setDecision] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [sinceDays, setSinceDays] = useState('7');
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    const since = sinceIso(sinceDays);

    try {
      const [decisions, counts, overridden] = await Promise.all([
        fetchPartnerDecisions({
          partner_id: partnerId,
          decision,
          since,
          limit: DECISION_PAGE_LIMIT,
        }),
        fetchPartnerDecisionSummary({ since }).catch(() => null),
        withDisagreements
          ? fetchPartnerDecisions({
            overridden: true,
            since: sinceIso(DISAGREEMENT_WINDOW_DAYS),
            limit: 1,
          }).catch(() => null)
          : null,
      ]);

      setRows(Array.isArray(decisions?.rows) ? decisions.rows : []);
      setTotal(decisions?.total ?? 0);
      setSummary(counts || EMPTY_SUMMARY);
      setDisagreements(overridden ? overridden.total : null);
    } catch (err) {
      if (onError) onError(err.message);
      setRows([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  }, [decision, partnerId, sinceDays, onError, withDisagreements]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * The partner filter's options, from the canonical partner list rather than
   * from the rows on screen. Derived from the rows it would narrow to one
   * option the moment somebody used it — the facet would eat itself. A
   * failure here costs the filter and nothing else, so it is swallowed.
   */
  useEffect(() => {
    let live = true;
    PartnersService.fetchPartners()
      .then((response) => {
        if (live) setPartners(response?.data?.data || []);
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  return {
    partners,
    rows,
    total,
    summary,
    disagreements,
    decision,
    setDecision,
    partnerId,
    setPartnerId,
    sinceDays,
    setSinceDays,
    isLoading,
    hasLoadedOnce,
    reload: load,
  };
}
